"use client"

import { ButtonHTMLAttributes, forwardRef } from "react"
import { cn } from "@/lib/utils"
import { Slot } from "@radix-ui/react-slot"

export interface GradientButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  variant?: "blue-red" | "red-blue" | "blue" | "red"
  size?: "sm" | "md" | "lg" | "xl"
}

const GradientButton = forwardRef<HTMLButtonElement, GradientButtonProps>(
  ({ className, variant = "blue-red", size = "md", asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    const sizeClasses = {
      sm: "h-9 px-4 text-sm",
      md: "h-10 px-6 text-base",
      lg: "h-11 px-8 text-base",
      xl: "h-12 px-10 text-lg",
    }

    const variantClasses = {
      "blue-red": "bg-gradient-to-r from-[#518BC4] via-[#6B7DBD] to-[#7C1414]",
      "red-blue": "bg-gradient-to-r from-[#7C1414] via-[#6B7DBD] to-[#518BC4]",
      "blue": "bg-gradient-to-r from-[#518BC4] to-[#4078B0]",
      "red": "bg-gradient-to-r from-[#7C1414] to-[#6B1010]",
    }

    return (
      <Comp
        ref={ref}
        className={cn(
          // Base styles
          "relative overflow-hidden rounded-lg font-semibold text-white",
          "inline-flex items-center justify-center gap-2",
          "whitespace-nowrap transition-all duration-500 ease-out",
          "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#518BC4]",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
          "shadow-md hover:shadow-xl",
          
          // Gradient background
          variantClasses[variant],
          "bg-[length:200%_100%] bg-left",
          
          // Hover animation - moves gradient
          "hover:bg-right",
          
          // Scale effect
          "hover:scale-[1.02] active:scale-[0.98]",
          
          // Transform origin
          "transform-gpu",
          
          // Size
          sizeClasses[size],
          
          // Before pseudo-element for shine effect
          "before:absolute before:inset-0 before:z-0",
          "before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent",
          "before:translate-x-[-200%] before:transition-transform before:duration-1000",
          "hover:before:translate-x-[200%]",
          
          className
        )}
        {...props}
      >
        <span className="relative z-10 inline-flex items-center justify-center gap-2">
          {children}
        </span>
      </Comp>
    )
  }
)

GradientButton.displayName = "GradientButton"

export { GradientButton }
